/**
 * usePermissionsByOrg — frontend-фільтр для OrgSelector у формах
 * (Порція UX permissions-by-org α, 2026-05-21).
 *
 * Закриває UX-діру: до цієї порції dropdown «Організація» у формах
 * ПКО/Реалізації/Платіжки тощо показував усі orgs accessible user'у
 * через user_access, але per-org permission міг бути відсутній → 403
 * на save (factory_body відкидав). Hook фільтрує список org по
 * `permissions_by_org` мапі з /auth/me — user бачить лише ті org, де
 * реально має необхідний permission для САМЕ ЦIЄЇ операції.
 *
 * Source of truth — backend (/auth/me повертає permissions_by_org).
 * Hook не вгадує permissions локально, а читає кешований response через
 * React Query (queryKey=['me']).
 *
 * Security: цей hook — UX-захист, не security. factory_body на backend
 * лишається активним; якщо frontend обійти, save все одно дасть 403.
 *
 * Використання:
 *   const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs, loading } =
 *     usePermissionsByOrg('cash.pko.manage');
 *   if (loading) return <Spinner />;
 *   if (!hasPermissionInAnyOrg) return <Alert>...</Alert>;
 *   // ... render <select>{availableOrgs.map(...)}</select>
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMe } from '@/api/auth';
import { orgs, type Organization } from '@/api/catalogs';

export interface UsePermissionsByOrgResult {
  /** Orgs де user реально має requiredPermission. */
  availableOrgs: Organization[];
  /** True доки /auth/me або /organizations ще завантажуються. */
  loading: boolean;
  /** True якщо `availableOrgs.length > 0`. */
  hasPermissionInAnyOrg: boolean;
  /**
   * Загальна к-сть accessible org (orgs повернутих /organizations).
   * Для UI-хінта «N з M ваших організацій».
   */
  totalAccessibleOrgs: number;
}

/**
 * Фільтрує список accessible orgs по permission_by_org map з /auth/me.
 *
 * @param requiredPermission код permission'а потрібного для операції
 *                           (наприклад 'cash.pko.manage', 'sales.realization.create')
 */
export function usePermissionsByOrg(
  requiredPermission: string,
): UsePermissionsByOrgResult {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe });
  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  const allOrgs = useMemo<Organization[]>(
    () => orgsQuery.data?.items ?? [],
    [orgsQuery.data],
  );

  const availableOrgs = useMemo<Organization[]>(() => {
    const pbo = meQuery.data?.permissions_by_org ?? {};
    return allOrgs.filter((o) => (pbo[o.id] ?? []).includes(requiredPermission));
  }, [allOrgs, meQuery.data, requiredPermission]);

  return {
    availableOrgs,
    loading: meQuery.isLoading || orgsQuery.isLoading,
    hasPermissionInAnyOrg: availableOrgs.length > 0,
    totalAccessibleOrgs: allOrgs.length,
  };
}


export interface UseAllPermissionsByOrgResult {
  /**
   * Raw permissions_by_org map з /auth/me — { "<org_id>": ["perm.a", "perm.b"] }.
   * Для випадків, де компонент має кілька different permissions і фільтрує
   * їх локально (наприклад, RefsPage з 3 tab'ами різних permission'ів через
   * один dropdown).
   */
  permissionsByOrg: Record<string, string[]>;
  /** Список усіх accessible org (orgs.list). */
  allOrgs: Organization[];
  /** True доки /auth/me або /organizations ще завантажуються. */
  loading: boolean;
}

/**
 * Multi-permission варіант usePermissionsByOrg (UX γ-essential 2026-05-21).
 *
 * Повертає raw permissions_by_org map + список усіх accessible org. Не
 * фільтрує, тільки експонує дані — споживач сам комбінує (наприклад
 * `permissionsByOrg[orgId]?.includes('refs.warehouses.edit')` per-tab).
 *
 * Використовуй коли:
 *  - У компонента кілька різних permission'ів через один org-dropdown
 *    (RefsPage tabs).
 *  - Треба «view+create» патерн: dropdown повний (read дозволено всім
 *    accessible через RLS), action-button per-permission disabled.
 *
 * Для одного permission краще `usePermissionsByOrg(perm)`.
 */
export function useAllPermissionsByOrg(): UseAllPermissionsByOrgResult {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe });
  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgs.list({ limit: 200 }),
  });

  const permissionsByOrg = useMemo<Record<string, string[]>>(
    () => meQuery.data?.permissions_by_org ?? {},
    [meQuery.data],
  );

  const allOrgs = useMemo<Organization[]>(
    () => orgsQuery.data?.items ?? [],
    [orgsQuery.data],
  );

  return {
    permissionsByOrg,
    allOrgs,
    loading: meQuery.isLoading || orgsQuery.isLoading,
  };
}
